/**
 * Notification playback (BUILD_SPEC.md §16.1, Milestone 7 step 57).
 *
 * Browser autoplay policy is the central design constraint. Both
 * Chromium and Gecko block `<audio>.play()` until the document has
 * received a user gesture; on a kiosk that runs unattended for hours,
 * the very first action that should trigger a chime may also be the
 * first thing a user *hasn't* clicked through. This module:
 *
 *   1. Pre-loads the three keyword tones lazily — the audio elements
 *      are constructed on first use, not at import time, so a tab
 *      that never plays a sound never costs network/CPU.
 *   2. Tracks the document's first interaction with capture-phase
 *      listeners on click/keydown/touchstart so the unlock signal
 *      arrives even if a route handler stops propagation.
 *   3. Exposes `isAudioUnlocked()` for the dashboard banner that
 *      prompts the user to enable sound when the page sat idle for
 *      ≥ 3 s with sound enabled.
 *   4. Deduplicates concurrent plays of the same keyword — three
 *      transactions flipping to APPROVAL in one poll cycle play one
 *      chime, not three (acceptance criterion 12).
 *   5. Gates on `document.visibilityState` so a hidden tab stays
 *      silent (acceptance criterion 11).
 *
 * Failure modes are handled defensively: a 404 on an audio file, a
 * codec error, or a play() rejection logs a `console.warn` and is
 * otherwise swallowed. The dashboard must never break because audio
 * was unavailable.
 */

import type { KeywordType } from "../../../../packages/keywords/index.js";

const AUDIO_FILES: Record<KeywordType, string> = {
  APPROVAL: "/sounds/approval.mp3",
  ASSIGNMENT: "/sounds/assignment.mp3",
  REJECTION: "/sounds/rejection.mp3",
};

let audioElements: Record<KeywordType, HTMLAudioElement> | null = null;
let interacted = false;
let interactionTracked = false;

/** Returns the lazily-created HTMLAudioElement map, building it on the
 *  first call. We use `<audio>` rather than the Web Audio API because
 *  these are one-shot short notification tones — Web Audio's gain
 *  graph adds complexity without buying any features we need. */
function ensureAudio(): Record<KeywordType, HTMLAudioElement> {
  if (audioElements) return audioElements;
  const map = {} as Record<KeywordType, HTMLAudioElement>;
  for (const keyword of Object.keys(AUDIO_FILES) as KeywordType[]) {
    const src = AUDIO_FILES[keyword];
    const el = new Audio(src);
    // `auto` lets the browser fetch the file eagerly once the element
    // exists. The files are small (< 20 KB each) so we don't pay a real
    // bandwidth cost for prefetching all three.
    el.preload = "auto";
    el.addEventListener("error", () => {
      // The console warning is the only surface for this failure — the
      // dashboard intentionally does not show a user-facing error for
      // a failed audio load because the loss-of-sound is non-blocking.
      console.warn(`[sounds] failed to load ${src}`);
    });
    map[keyword] = el;
  }
  audioElements = map;
  return map;
}

/** Marks the document as having had a user gesture, satisfying the
 *  autoplay-unlock requirement. Idempotent and listener-detaching so
 *  the cost is one-time. */
function markInteracted(): void {
  if (interacted) return;
  interacted = true;
  document.removeEventListener("click", markInteracted, true);
  document.removeEventListener("keydown", markInteracted, true);
  document.removeEventListener("touchstart", markInteracted, true);
}

/** Starts listening for the first user gesture on the document. Safe
 *  to call multiple times — `interactionTracked` keeps it a no-op
 *  after the first call. */
export function startInteractionTracking(): void {
  if (interactionTracked) return;
  interactionTracked = true;
  // Capture phase so a child handler's `stopPropagation` doesn't
  // suppress the unlock signal. Anything that counts as a gesture in
  // the browser's autoplay-policy implementation also fires one of
  // these three events.
  document.addEventListener("click", markInteracted, true);
  document.addEventListener("keydown", markInteracted, true);
  document.addEventListener("touchstart", markInteracted, true);
}

/** True once the user has interacted with the document at least once
 *  during this session — the dashboard banner uses this to know when
 *  it can stop nagging. */
export function isAudioUnlocked(): boolean {
  return interacted;
}

export interface PlaybackPolicy {
  masterEnabled: boolean;
  perKeywordEnabled: Record<KeywordType, boolean>;
  /** 0–100 integer, mapped to HTMLMediaElement.volume / 100. */
  volume: number;
}

/**
 * Plays the tone for `keyword`, respecting all four gates from
 * §16.1: master mute, per-keyword toggle, document visibility, and
 * browser autoplay policy. Returns silently if any gate blocks
 * playback so callers don't need to recheck the rules themselves.
 *
 * @param keyword the keyword that just landed on a card
 * @param policy effective playback rules — typically derived from the
 *   sound store inside the calling component
 */
export function playKeywordSound(keyword: KeywordType, policy: PlaybackPolicy): void {
  // v1.1 A4: each gate logs a single debug line when it blocks
  // playback. Devtools-only — no user-facing surface — so the noise
  // budget is zero in normal operation. When Omar reports a missed
  // chime, opening devtools and grepping for "[sounds]" tells him
  // exactly which condition suppressed the tone.
  if (!policy.masterEnabled) {
    console.debug(`[sounds] ${keyword} suppressed: master mute is off`);
    return;
  }
  if (!policy.perKeywordEnabled[keyword]) {
    console.debug(`[sounds] ${keyword} suppressed: per-keyword toggle is off`);
    return;
  }
  // §16.1 / acceptance criterion 11: a hidden tab is silent. Updating
  // the seen-action set still happens in the caller, so when the user
  // returns we don't re-fire sounds for actions that landed mid-hide.
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    console.debug(`[sounds] ${keyword} suppressed: tab is hidden`);
    return;
  }
  // No gesture yet: the banner is the user-facing nag for this case.
  if (!interacted) {
    console.debug(`[sounds] ${keyword} suppressed: audio not unlocked (no user gesture yet)`);
    return;
  }

  const el = ensureAudio()[keyword];
  el.volume = Math.max(0, Math.min(1, policy.volume / 100));
  // Reset to start so a previously-completed play retriggers cleanly.
  el.currentTime = 0;
  const result = el.play();
  // play() returns a Promise on every modern browser. Catch is
  // important — a rejection (autoplay race, missing codec) must not
  // surface as an unhandled-promise warning in the console.
  if (result && typeof result.catch === "function") {
    result.catch((err: unknown) => {
      console.warn(`[sounds] play() rejected for ${keyword}:`, err);
    });
  }
}

/**
 * v1.1 C5 — fires the rejection tone at low volume as an "error"
 * sound for UI affordances that aren't notifications (e.g. shaking
 * the Add Transaction modal when the user clicks the backdrop).
 * Bypasses the sound-store policy because this is direct feedback to
 * a user gesture, not a chime; still respects the audio-unlock state
 * since browsers block playback without a prior interaction.
 *
 * Volume is hardcoded to 0.3 — half of the default per-keyword volume,
 * loud enough to register on a quiet desk but not jarring.
 */
export function playErrorTone(): void {
  if (!interacted) return;
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
  const el = ensureAudio().REJECTION;
  el.volume = 0.3;
  el.currentTime = 0;
  const result = el.play();
  if (result && typeof result.catch === "function") {
    result.catch((err: unknown) => {
      console.debug("[sounds] error tone play() rejected:", err);
    });
  }
}

/**
 * Test-only escape hatch to reset the module-level state between
 * runs. The dashboard never calls this; it exists so unit tests can
 * exercise the interaction-tracking state machine deterministically.
 */
export function __resetSoundsForTests(): void {
  audioElements = null;
  interacted = false;
  interactionTracked = false;
}
